import type { ReactNode } from 'react'
import { useEffect } from 'react'
import { X, Filter, RotateCcw, Check } from 'lucide-react'
import { Button } from './Button'
import { cn } from '@/lib/cn'

interface Props {
  open: boolean
  onClose: () => void
  onApply?: () => void
  onClear?: () => void
  activeCount?: number
  title?: ReactNode
  children: ReactNode
}

/**
 * Drawer de filtros — slide-over da direita.
 *  - sm: 420px largura, full-height
 *  - <sm: full-screen
 *  - Design plano (sem shadow), border-l forte para se destacar do fundo
 */
export function FilterDrawer({ open, onClose, onApply, onClear, activeCount, title = 'Filtros', children }: Props) {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [open, onClose])

  return (
    <>
      <div
        aria-hidden={!open}
        onClick={onClose}
        className={cn(
          'fixed inset-0 z-40 bg-black/30 transition-opacity',
          open ? 'opacity-100' : 'opacity-0 pointer-events-none',
        )}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : 'Filtros'}
        className={cn(
          'fixed inset-y-0 right-0 z-50 w-full sm:w-[420px] max-w-[100vw] bg-white ring-1 ring-gray-300 border-l border-gray-200 flex flex-col transition-transform duration-200 ease-out',
          open ? 'translate-x-0' : 'translate-x-full',
        )}
      >
        <header className="flex items-center justify-between gap-3 px-5 py-4 border-b border-gray-200 shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <Filter size={16} className="text-gray-500 shrink-0" />
            <h3 className="text-base font-semibold text-gray-800 break-words">{title}</h3>
            {!!activeCount && activeCount > 0 && (
              <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-brand-500 text-white text-[11px] font-semibold">
                {activeCount}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="cursor-pointer p-2 -mr-1 rounded-md hover:bg-gray-100 text-gray-400 hover:text-gray-600"
          >
            <X size={18} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4">{children}</div>

        {(onApply || onClear) && (
          <footer className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-200 bg-gray-50/60 shrink-0">
            {onClear && (
              <Button variant="ghost" onClick={onClear} iconLeft={<RotateCcw size={12} />}>
                Limpar
              </Button>
            )}
            {onApply && (
              <Button onClick={() => { onApply(); onClose() }} iconLeft={<Check size={12} />}>
                Aplicar
              </Button>
            )}
          </footer>
        )}
      </aside>
    </>
  )
}
